import type { JSX } from "react";

import { Text } from "@mantine/core";

import type { UseDeviceActivation } from "../device-activation";

interface ActivationNoticesProps {
    activation: UseDeviceActivation;
    isLoading: boolean;
    showAskAssist: boolean;
    showDeviceConfirmation: boolean;
}

export function ActivationNotices({
    activation,
    isLoading,
    showAskAssist,
    showDeviceConfirmation,
}: ActivationNoticesProps): JSX.Element {
    const {
        blockedReason,
        deviceLabel,
        failureReason,
        isActivationBlocked,
        isActivationSubmitStep,
        isVerizon,
        recoveryNotice,
        verizonReplacement,
    } = activation;

    return (
        <>
            {recoveryNotice && (
                <Text c="red" size="sm" mt="xs">
                    {recoveryNotice}
                </Text>
            )}
            {failureReason && (
                <Text c="red" size="sm" mt="xs">
                    <b>Why activation didn&apos;t complete:</b>
                    <br />
                    <Text span inherit className="fs-mask">
                        {failureReason}
                    </Text>
                </Text>
            )}
            {isActivationBlocked && isActivationSubmitStep && !isLoading && (
                <Text c="red" size="sm" mt="xs">
                    <b>This activation can&apos;t be retried as-is.</b>
                    <br />
                    <Text span inherit className="fs-mask">
                        {blockedReason}
                    </Text>
                    <br />
                    {isVerizon
                        ? "Verizon won't accept this activation from here. Finish it in ACSS."
                        : "Once the issue is resolved, go back and re-verify to send a new code, or change an answer in a previous step, then try again."}
                </Text>
            )}
            {isVerizon && verizonReplacement && isActivationSubmitStep && !isActivationBlocked && (
                <Text c="dark.0" size="sm" mt="xs" mb="sm">
                    Confirm with the customer that they have the{" "}
                    <b className="fs-mask">{`${verizonReplacement.make} ${verizonReplacement.model}`.trim()}</b> for the
                    line ending in <b className="fs-mask">{verizonReplacement.lineLast4}</b>, and that it&apos;s powered
                    on
                    {verizonReplacement.eSim ? "." : " with the SIM card from the replacement kit inserted."}
                </Text>
            )}
            {showDeviceConfirmation && deviceLabel && (
                <Text c="dark.0" size="sm" mt="xs" mb="sm">
                    We see the customer may have a <b>{deviceLabel}</b> on their account — confirm this is the device
                    before continuing.
                </Text>
            )}
            {showAskAssist && deviceLabel && (
                <Text c="dark.0" size="sm" mt="xs" mb="sm">
                    We&apos;re detecting the customer&apos;s device as <b>{deviceLabel}</b>.
                </Text>
            )}
        </>
    );
}
